import React, { memo, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { getCountryName } from '@/lib/translations';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '@/lib/utils';

// Country slug to name mapping (fallback)
const COUNTRY_NAMES = {
  'austria': 'Austria', 'belgium': 'Belgium', 'bulgaria': 'Bulgaria', 'croatia': 'Croatia',
  'cyprus': 'Cyprus', 'czech-republic': 'Czech Republic', 'denmark': 'Denmark', 'estonia': 'Estonia',
  'finland': 'Finland', 'france': 'France', 'germany': 'Germany', 'greece': 'Greece',
  'hungary': 'Hungary', 'ireland': 'Ireland', 'italy': 'Italy', 'latvia': 'Latvia',
  'lithuania': 'Lithuania', 'luxembourg': 'Luxembourg', 'malta': 'Malta', 'netherlands': 'Netherlands',
  'poland': 'Poland', 'portugal': 'Portugal', 'romania': 'Romania', 'slovakia': 'Slovakia',
  'slovenia': 'Slovenia', 'spain': 'Spain', 'sweden': 'Sweden', 'united-kingdom': 'United Kingdom',
  'switzerland': 'Switzerland', 'norway': 'Norway', 'iceland': 'Iceland', 'serbia': 'Serbia',
  'bosnia': 'Bosnia', 'albania': 'Albania', 'north-macedonia': 'North Macedonia',
  'montenegro': 'Montenegro', 'ukraine': 'Ukraine', 'turkey': 'Turkey', 'australia': 'Australia',
  'new-zealand': 'New Zealand', 'canada': 'Canada', 'india': 'India', 'japan': 'Japan',
  'south-korea': 'South Korea', 'singapore': 'Singapore', 'malaysia': 'Malaysia',
  'thailand': 'Thailand', 'philippines': 'Philippines', 'indonesia': 'Indonesia',
  'vietnam': 'Vietnam', 'south-africa': 'South Africa', 'brazil': 'Brazil', 'mexico': 'Mexico',
  'argentina': 'Argentina', 'chile': 'Chile', 'colombia': 'Colombia', 'uae': 'UAE',
  'saudi-arabia': 'Saudi Arabia', 'israel': 'Israel', 'egypt': 'Egypt', 'nigeria': 'Nigeria',
  'kenya': 'Kenya'
};

// Comprehensive breadcrumb translations
const BREADCRUMB_TRANSLATIONS = {
  en: {
    home: 'Home',
    calculator: 'VAT Calculator',
    blog: 'Blog',
    rates: 'VAT Rates',
    privacy: 'Privacy Policy',
    cookies: 'Cookie Policy',
    about: 'About',
    contact: 'Contact'
  },
  de: {
    home: 'Startseite',
    calculator: 'MwSt Rechner',
    blog: 'Blog',
    rates: 'MwSt Sätze',
    privacy: 'Datenschutz',
    cookies: 'Cookie-Richtlinie',
    about: 'Über uns',
    contact: 'Kontakt'
  },
  fr: {
    home: 'Accueil',
    calculator: 'Calculateur TVA',
    blog: 'Blog',
    rates: 'Taux TVA',
    privacy: 'Politique de confidentialité',
    cookies: 'Politique des cookies',
    about: 'À propos',
    contact: 'Contact'
  },
  it: {
    home: 'Home',
    calculator: 'Calcolatore IVA',
    blog: 'Blog',
    rates: 'Aliquote IVA',
    privacy: 'Privacy Policy',
    cookies: 'Cookie Policy',
    about: 'Chi siamo',
    contact: 'Contatti'
  },
  es: {
    home: 'Inicio',
    calculator: 'Calculadora IVA',
    blog: 'Blog',
    rates: 'Tipos IVA',
    privacy: 'Política de Privacidad',
    cookies: 'Política de Cookies',
    about: 'Acerca de',
    contact: 'Contacto'
  }
};

/**
 * Breadcrumbs Component - SEO optimized with Schema.org markup
 *
 * Features:
 * - Visible navigation breadcrumbs for users
 * - Schema.org BreadcrumbList JSON-LD for search engines
 * - Microdata attributes for additional SEO benefit
 * - Multi-language support
 * - Accessible with ARIA labels
 *
 * @param {Object} props
 * @param {Object} [props.country] - Country object with code and tax_name
 * @param {string} [props.page] - Page type: 'blog', 'rates', 'privacy', 'cookies', 'about', 'contact'
 * @param {string} [props.blogTitle] - Title of blog post (when page='blog')
 * @param {string} [props.pageType] - Deprecated: use 'page' instead
 * @param {string} [props.className] - Additional CSS classes
 */
const Breadcrumbs = memo(({
  country = null,
  page = null,
  pageType = null, // backwards compatibility
  blogTitle = null,
  className = ''
}) => {
  const { language } = useLanguage();
  const location = useLocation();

  const t = BREADCRUMB_TRANSLATIONS[language] || BREADCRUMB_TRANSLATIONS.en;
  const langPrefix = language === 'en' ? '' : `/${language}`;

  // Normalize page type (support both 'page' and legacy 'pageType' prop)
  const normalizedPage = page || pageType || 'calculator';

  // Build breadcrumb items with memoization
  const items = useMemo(() => {
    const breadcrumbs = [];

    // Home is always first
    breadcrumbs.push({
      label: t.home,
      href: langPrefix || '/',
      icon: Home,
      current: false
    });

    // Add page-specific breadcrumbs
    switch (normalizedPage) {
      case 'blog':
        // Blog section
        if (blogTitle) {
          // Blog article page: Home > Blog > Article Title
          breadcrumbs.push({
            label: t.blog,
            href: `${langPrefix}/blog`,
            current: false
          });
          breadcrumbs.push({
            label: blogTitle,
            href: location.pathname,
            current: true
          });
        } else {
          // Blog index page: Home > Blog
          breadcrumbs.push({
            label: t.blog,
            href: `${langPrefix}/blog`,
            current: true
          });
        }
        break;

      case 'rates':
        // VAT Rates page: Home > VAT Rates
        breadcrumbs.push({
          label: t.rates,
          href: `${langPrefix}/rates`,
          current: true
        });
        break;

      case 'privacy':
        // Privacy page: Home > Privacy Policy
        breadcrumbs.push({
          label: t.privacy,
          href: `${langPrefix}/privacy`,
          current: true
        });
        break;

      case 'cookies':
        // Cookie page: Home > Cookie Policy
        breadcrumbs.push({
          label: t.cookies,
          href: `${langPrefix}/cookies`,
          current: true
        });
        break;

      case 'about':
        // About page: Home > About
        breadcrumbs.push({
          label: t.about,
          href: `${langPrefix}/about`,
          current: true
        });
        break;

      case 'contact':
        // Contact page: Home > Contact
        breadcrumbs.push({
          label: t.contact,
          href: `${langPrefix}/contact`,
          current: true
        });
        break;

      default:
        // Calculator pages
        if (country) {
          // Country-specific calculator: Home > VAT Calculator > Country
          const localizedCountryName = getCountryName(country.code, language) ||
                                       COUNTRY_NAMES[country.code] ||
                                       country.name;
          const taxName = country.tax_name || 'VAT';

          breadcrumbs.push({
            label: t.calculator,
            href: langPrefix || '/'
          });
          breadcrumbs.push({
            label: `${localizedCountryName} ${taxName}`,
            href: location.pathname,
            current: true
          });
        } else {
          // Homepage calculator: Home > VAT Calculator
          breadcrumbs.push({
            label: t.calculator,
            href: langPrefix || '/',
            current: true
          });
        }
        break;
    }

    return breadcrumbs;
  }, [t, langPrefix, normalizedPage, blogTitle, country, language, location.pathname]);

  // Generate Schema.org BreadcrumbList JSON-LD
  const schemaData = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.label,
      "item": `https://vat.expert${item.href}`
    }))
  }), [items]);

  // Don't render if only home breadcrumb
  if (items.length < 2) {
    return null;
  }

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn("w-full mb-4", className)}
    >
      {/* Schema.org JSON-LD markup for search engines */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      {/* Visible breadcrumbs with microdata */}
      <ol
        className="flex items-center gap-1 text-sm text-muted-foreground flex-wrap"
        itemScope
        itemType="https://schema.org/BreadcrumbList"
      >
        {items.map((item, index) => (
          <li
            key={`breadcrumb-${index}`}
            className="flex items-center"
            itemScope
            itemProp="itemListElement"
            itemType="https://schema.org/ListItem"
          >
            {/* Separator (chevron) between items */}
            {index > 0 && (
              <ChevronRight
                className="w-4 h-4 mx-1 text-muted-foreground/50 flex-shrink-0"
                aria-hidden="true"
              />
            )}

            {item.current ? (
              // Current page - not a link
              <span
                className={cn(
                  "flex items-center gap-1.5 font-medium text-foreground",
                  "max-w-[250px] truncate"
                )}
                aria-current="page"
                itemProp="name"
              >
                {item.icon && (
                  <item.icon
                    className="w-4 h-4 flex-shrink-0"
                    aria-hidden="true"
                  />
                )}
                {item.label}
              </span>
            ) : (
              // Clickable breadcrumb link
              <Link
                to={item.href}
                className={cn(
                  "flex items-center gap-1.5",
                  "hover:text-primary transition-colors",
                  "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded",
                  "max-w-[200px] truncate"
                )}
                itemProp="item"
              >
                {item.icon && (
                  <item.icon
                    className="w-4 h-4 flex-shrink-0"
                    aria-hidden="true"
                  />
                )}
                <span itemProp="name">{item.label}</span>
              </Link>
            )}

            {/* Position meta for Schema.org microdata */}
            <meta itemProp="position" content={String(index + 1)} />
          </li>
        ))}
      </ol>
    </nav>
  );
});

Breadcrumbs.displayName = 'Breadcrumbs';

export default Breadcrumbs;
